import React, { useState, useCallback } from 'react';
import { Users, Activity, Clock, Image, Film, FileText, RefreshCw, Lock, Eye, EyeOff } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { API_HOST } from '../utils/constants';
import { getPublicVideoLabel } from '../utils/projectRuntime';
import { useSequentialPolling } from '../hooks/useSequentialPolling';
import { authenticatedFetch } from '../utils/api';

const getStoredAdminSecret = () => (
  typeof sessionStorage === 'undefined' ? '' : (sessionStorage.getItem('admin_secret') || '')
);

const UserActivityPage = () => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  const [authenticated, setAuthenticated] = useState(() => Boolean(getStoredAdminSecret()));
  const [secretInput, setSecretInput] = useState('');
  const [secretError, setSecretError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (secret, { signal } = {}) => {
    try {
      const activity = await authenticatedFetch(
        `${API_HOST}/api/admin/user-activity?secret=${encodeURIComponent(secret)}`,
        { signal, timeout: 45_000 },
      );
      if (signal?.aborted) return;
      setData(activity);
      setError(null);
    } catch (err) {
      if (signal?.aborted) return;
      if (err?.code === 403 || err?.status === 403) {
        sessionStorage.removeItem('admin_secret');
        setAuthenticated(false);
        setSecretError('密码错误，请重新输入');
        return;
      }
      setError(err.message);
    }
  }, []);

  const pollActivity = useCallback(
    ({ signal } = {}) => fetchData(getStoredAdminSecret(), { signal }),
    [fetchData],
  );
  useSequentialPolling(pollActivity, 5000, { enabled: authenticated });

  // 密码提交
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!secretInput.trim()) return;
    setLoading(true);
    setSecretError('');
    try {
      const activity = await authenticatedFetch(
        `${API_HOST}/api/admin/user-activity?secret=${encodeURIComponent(secretInput.trim())}`,
        { timeout: 45_000 },
      );
      sessionStorage.setItem('admin_secret', secretInput.trim());
      setAuthenticated(true);
      setData(activity);
      setError(null);
    } catch (err) {
      setSecretError(err?.code === 403 || err?.status === 403
        ? '密码错误'
        : '网络错误: ' + err.message);
    }
    setLoading(false);
  };

  // 密码输入页
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.colors.bgPrimary }}>
        <form onSubmit={handleLogin} className="w-80 p-6 rounded-xl border" style={{ backgroundColor: theme.colors.bgSecondary, borderColor: theme.colors.borderPrimary }}>
          <div className="flex items-center gap-2 mb-6 justify-center">
            <Lock size={24} style={{ color: theme.colors.accentPrimary }} />
            <span className="text-lg font-bold" style={{ color: theme.colors.textPrimary }}>管理员验证</span>
          </div>
          <div className="relative mb-4">
            <input
              type={showPassword ? 'text' : 'password'}
              value={secretInput}
              onChange={e => setSecretInput(e.target.value)}
              placeholder="请输入管理员密码"
              autoFocus
              className="w-full px-3 py-2 pr-10 rounded-lg border text-sm outline-none"
              style={{
                backgroundColor: theme.colors.bgTertiary,
                borderColor: secretError ? 'var(--color-error)' : theme.colors.borderSecondary,
                color: theme.colors.textPrimary,
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 opacity-50 hover:opacity-100 transition-opacity"
              style={{ color: theme.colors.textMuted }}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {secretError && <p className="text-xs mb-3" style={{ color: 'var(--color-error)' }}>{secretError}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 rounded-lg text-sm font-medium transition-opacity"
            style={{ backgroundColor: theme.colors.accentPrimary, color: 'var(--color-bg-primary)', opacity: loading ? 0.6 : 1 }}
          >
            {loading ? '验证中...' : '进入'}
          </button>
        </form>
      </div>
    );
  }

  // 加载中
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.colors.bgPrimary }}>
        <div className="text-center">
          <RefreshCw size={32} className="animate-spin mx-auto mb-3" style={{ color: theme.colors.accentPrimary }} />
          <p style={{ color: theme.colors.textSecondary }}>加载中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.colors.bgPrimary }}>
        <p style={{ color: theme.colors.error }}>{error}</p>
      </div>
    );
  }

  const { users, summary } = data;

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: theme.colors.bgPrimary }}>
      {/* 标题 */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Users size={28} style={{ color: theme.colors.accentPrimary }} />
          <h1 className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>用户活动监控</h1>
        </div>
        <p className="text-sm" style={{ color: theme.colors.textMuted }}>实时追踪用户资源占用 · 每5秒刷新</p>
      </div>

      {/* 汇总卡片 */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <SummaryCard icon={<Users size={20} />} label="活跃用户" value={summary.totalActiveUsers} color={theme.colors.accentPrimary} theme={theme} />
        <SummaryCard icon={<Activity size={20} />} label="处理中" value={summary.totalProcessing} color={'var(--color-success)'} theme={theme} />
        <SummaryCard icon={<Clock size={20} />} label="排队中" value={summary.totalQueued} color={'var(--color-warning)'} theme={theme} />
      </div>

      {/* 用户卡片网格 */}
      {users.length === 0 ? (
        <div className="text-center py-16" style={{ color: theme.colors.textMuted }}>暂无用户数据</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {users.map((user) => (
            <UserCard key={user.userId} user={user} theme={theme} />
          ))}
        </div>
      )}
    </div>
  );
};

// 汇总卡片
const SummaryCard = ({ icon, label, value, color, theme }) => (
  <div className="p-4 rounded-xl border" style={{ backgroundColor: theme.colors.bgSecondary, borderColor: theme.colors.borderPrimary }}>
    <div className="flex items-center gap-2 mb-1">
      <div style={{ color }}>{icon}</div>
      <span className="text-xs" style={{ color: theme.colors.textMuted }}>{label}</span>
    </div>
    <div className="text-2xl font-bold" style={{ color }}>{value}</div>
  </div>
);

// 用户卡片
const UserCard = ({ user, theme }) => {
  const hasActive = user.activeTasks && user.activeTasks.length > 0;
  const hasRealtime = !!user.realtimeInfo;

  return (
    <div
      className="p-4 rounded-xl border transition-colors"
      style={{
        backgroundColor: theme.colors.bgSecondary,
        borderColor: (hasActive || hasRealtime) ? (theme.colors.accentPrimary + '60') : theme.colors.borderPrimary,
      }}
    >
      {/* 用户名 */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-base font-bold" style={{ color: theme.colors.textPrimary }}>
          {decodeUser(user.userId)}
        </span>
        {(hasActive || hasRealtime) && (
          <span
            className="text-2xs px-2 py-0.5 rounded-full font-medium"
            style={{ backgroundColor: 'color-mix(in srgb, var(--color-success) 13%, transparent)', color: 'var(--color-success)' }}
          >
            活跃
          </span>
        )}
      </div>

      {/* 实时信息 */}
      {hasRealtime && (
        <div className="mb-3 p-2 rounded-lg text-xs" style={{ backgroundColor: theme.colors.bgTertiary }}>
          <div className="flex items-center gap-1 mb-1">
            <Activity size={12} style={{ color: theme.colors.accentPrimary }} />
            <span style={{ color: theme.colors.accentPrimary }}>{user.realtimeInfo.taskType} · {user.realtimeInfo.step}</span>
            <span className="ml-auto" style={{ color: theme.colors.textMuted }}>{user.realtimeInfo.time}</span>
          </div>
          {user.realtimeInfo.details && (
            <p className="truncate" style={{ color: theme.colors.textSecondary }}>{user.realtimeInfo.details}</p>
          )}
        </div>
      )}

      {/* 活跃任务列表 */}
      {hasActive && (
        <div className="mb-3 space-y-2">
          {user.activeTasks.map((task) => (
            <TaskRow key={task.taskId} task={task} theme={theme} />
          ))}
        </div>
      )}

      {/* 统计 */}
      <div className="flex gap-4 text-xs pt-2" style={{ borderTop: `1px solid ${theme.colors.borderSecondary}` }}>
        <StatGroup label="今日" stats={user.todayStats} theme={theme} />
        <StatGroup label="累计" stats={user.totalStats} theme={theme} />
      </div>
    </div>
  );
};

// 任务行
const TaskRow = ({ task, theme }) => {
  const statusColors = {
    processing: 'var(--color-success)',
    queued: 'var(--color-warning)',
  };
  const color = statusColors[task.status] || theme.colors.textMuted;
  const statusText = task.status === 'processing' ? '处理中' : '排队中';

  return (
    <div className="p-2 text-xs">
      <div className="flex items-center gap-2 mb-1">
        <span className="font-medium" style={{ color: theme.colors.textPrimary }}>{formatTaskType(task.taskType)}</span>
        {task.provider && <span style={{ color: theme.colors.textMuted }}>{getPublicVideoLabel(task.provider, task.provider)}</span>}
        <span className="ml-auto px-1.5 py-0.5 rounded text-2xs font-medium" style={{ backgroundColor: color + '20', color }}>{statusText}</span>
      </div>
      {task.stage && <p className="mb-1" style={{ color: theme.colors.textSecondary }}>{task.stage}</p>}
      {task.status === 'processing' && task.progress != null && task.progress > 0 && (
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: theme.colors.bgPrimary }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${task.progress}%`, backgroundColor: color }} />
        </div>
      )}
    </div>
  );
};

// 统计组
const StatGroup = ({ label, stats, theme }) => (
  <div className="flex-1">
    <span className="block mb-1 font-medium" style={{ color: theme.colors.textMuted }}>{label}</span>
    <div className="flex gap-3">
      <span className="flex items-center gap-1" style={{ color: theme.colors.textSecondary }}>
        <Image size={11} /> {stats?.images || 0}
      </span>
      <span className="flex items-center gap-1" style={{ color: theme.colors.textSecondary }}>
        <Film size={11} /> {stats?.videos || 0}
      </span>
      <span className="flex items-center gap-1" style={{ color: theme.colors.textSecondary }}>
        <FileText size={11} /> {stats?.scripts || 0}
      </span>
    </div>
  </div>
);

// 工具函数
const ENCODED_OCTET_RE = /%[0-9A-Fa-f]{2}/;

function decodeUser(userId) {
  if (!userId) return '未知用户';
  let text = String(userId);
  for (let i = 0; i < 3 && ENCODED_OCTET_RE.test(text); i += 1) {
    try {
      const next = decodeURIComponent(text);
      if (next === text) break;
      text = next;
    } catch {
      break;
    }
  }
  return text || '未知用户';
}

function formatTaskType(type) {
  const map = {
    'TEXT_AND_REFERENCE_TO_IMAGE': '图片生成',
    'TEXT_TO_IMAGE': '文生图',
    'IMAGE_TO_IMAGE': '图生图',
    'CREATE_STORYBOARD': '分镜生成',
    'TEXT_TO_VIDEO': '文生视频',
    'IMAGE_TO_VIDEO': '图生视频',
  };
  return map[type] || type;
}

export default UserActivityPage;
